// src/components/auth/Login.js
import React, { useState } from "react";
import { auth, firestore } from "../../utils/firebase-config";
import { GoogleAuthProvider, signInWithPopup, signInWithEmailAndPassword } from "firebase/auth";
import { doc, setDoc, getDoc, updateDoc } from 'firebase/firestore';
import { COLLECTION_NAMES } from "../../config/firestore-collections";
import {
  Container,
  Box,
  Typography,
  TextField,
  Button,
  Paper,
  Alert,
  CircularProgress,
  InputAdornment,
  IconButton
} from "@mui/material";
import GoogleIcon from "@mui/icons-material/Google";
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import Register from "./Register";
import ResetPassword from "./ResetPassword";

function Login() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Estados para vistas alternas
  const [isRegistering, setIsRegistering] = useState(false);
  const [showResetPassword, setShowResetPassword] = useState(false);

  const handleMouseDownPassword = (event) => {
    event.preventDefault();
  };

  const signInWithEmail = async (e) => {
    e.preventDefault(); // Prevenir reload si se dispara por submit de form
    setLoading(true);
    setError(null);
    try {
      const userCredential = await signInWithEmailAndPassword(auth, email, password);
      const user = userCredential.user;
      // setCurrentUser se maneja automáticamente por UserContext a través de onAuthStateChanged

      const userDocRef = doc(firestore, COLLECTION_NAMES.users, user.uid);
      const docSnap = await getDoc(userDocRef);

      if (docSnap.exists()) {
        // Solo actualizar lastLogin, el rol lo maneja UserContext
        await updateDoc(userDocRef, {
          lastLogin: new Date()
        });
      } else {
        // Crear usuario si no existe
        await setDoc(userDocRef, {
          email: user.email,
          role: 'pendiente_de_asignacion',
          lastLogin: new Date(),
          createdAt: new Date()
        });
      }

      // No navegar ni establecer rol aquí - UserContext manejará todo automáticamente
      // AppRoutes redirigirá según el rol que UserContext obtenga
    } catch (error) {
      let msg = "Error de autenticación.";
      if (error.code === 'auth/wrong-password') msg = "Contraseña incorrecta.";
      if (error.code === 'auth/user-not-found') msg = "Usuario no encontrado.";
      if (error.code === 'auth/invalid-email') msg = "Email inválido.";
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  const signInWithGoogle = async () => {
    const provider = new GoogleAuthProvider();

    // Agregar scopes específicos para asegurar acceso a perfil y email
    provider.addScope('profile');
    provider.addScope('email');

    setLoading(true);
    setError(null);
    try {
      const result = await signInWithPopup(auth, provider);
      const user = result.user;

      // setCurrentUser se maneja automáticamente por UserContext a través de onAuthStateChanged

      const userDocRef = doc(firestore, COLLECTION_NAMES.users, user.uid);
      const docSnap = await getDoc(userDocRef);

      if (!docSnap.exists()) {
        // Crear usuario si no existe
        await setDoc(userDocRef, {
          email: user.email,
          role: 'pendiente_de_asignacion',
          lastLogin: new Date(),
          createdAt: new Date()
        });
      } else {
        // Solo actualizar lastLogin, el rol lo maneja UserContext
        await updateDoc(userDocRef, {
          lastLogin: new Date()
        });
      }

      // No navegar ni establecer rol aquí - UserContext manejará todo automáticamente
      // AppRoutes redirigirá según el rol que UserContext obtenga
    } catch (error) {
      console.error('❌ Error completo:', error);
      console.error('❌ Error code:', error.code);
      console.error('❌ Error message:', error.message);
      if (error.customData) {
        console.error('❌ Error customData:', error.customData);
      }

      let errorMsg = "Error iniciando sesión con Google: ";

      // Mensajes de error más específicos
      switch (error.code) {
        case 'auth/popup-closed-by-user':
          errorMsg += "Popup cerrado. Por favor intenta de nuevo.";
          break;
        case 'auth/popup-blocked':
          errorMsg += "El popup fue bloqueado por el navegador. Por favor permite popups para este sitio.";
          break;
        case 'auth/internal-error':
          errorMsg += "Error de configuración. Verifica que Google esté habilitado en Firebase Console y que 'localhost' esté en dominios autorizados.";
          if (process.env.NODE_ENV === 'development') {
            console.error('⚠️ SOLUCIÓN: Ve a Firebase Console → Authentication → Sign-in method → Google (debe estar habilitado)');
            console.error('⚠️ SOLUCIÓN: Ve a Firebase Console → Authentication → Settings → Authorized domains (debe incluir localhost)');
          }
          break;
        case 'auth/unauthorized-domain':
          errorMsg += "Dominio no autorizado. Agrega 'localhost' a los dominios autorizados en Firebase Console.";
          break;
        default:
          errorMsg += `${error.code} - ${error.message}`;
      }

      setError(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  const switchMode = () => {
    setIsRegistering(!isRegistering);
    setError(null);
    setShowResetPassword(false);
  };

  const toggleResetPassword = () => {
    setShowResetPassword(!showResetPassword);
    setError(null);
  };

  // Handlers para cuando el registro/reset termina
  const handleRegisterSuccess = () => {
    setIsRegistering(false);
    setError(null);
    // Podríamos mostrar un mensaje de éxito aquí
  };

  return (
    <Container component="main" maxWidth="sm">
      <Paper elevation={3} sx={{ mt: 8, p: 4, borderRadius: 2 }}>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
          }}
        >
          {/* Título dinámico */}
          <Typography component="h1" variant="h5" sx={{ mb: 2, fontWeight: 600 }}>
            {showResetPassword
              ? "Recuperar Contraseña"
              : isRegistering
                ? "Crear Cuenta"
                : "Iniciar Sesión"
            }
          </Typography>

          {/* Mensaje de error general */}
          {error && (
            <Alert severity="error" sx={{ width: '100%', mb: 2 }}>
              {error}
            </Alert>
          )}

          {/* Renderizado Condicional de Vistas */}
          {showResetPassword ? (
            <Box sx={{ width: '100%' }}>
              <ResetPassword onBack={() => setShowResetPassword(false)} />
              <Button fullWidth onClick={() => setShowResetPassword(false)} sx={{ mt: 1 }}>
                Volver al Login
              </Button>
            </Box>
          ) : isRegistering ? (
            <Box sx={{ width: '100%' }}>
              <Register onRegistered={handleRegisterSuccess} onBack={() => setIsRegistering(false)} />
              <Button fullWidth onClick={() => setIsRegistering(false)} sx={{ mt: 1 }}>
                ¿Ya tienes cuenta? Inicia Sesión
              </Button>
            </Box>
          ) : (
            /* LOGIN FORM */
            <Box component="form" onSubmit={signInWithEmail} sx={{ mt: 1, width: '100%' }}>
              <TextField
                margin="normal"
                required
                fullWidth
                id="email"
                label="Dirección de Email"
                name="email"
                autoComplete="email"
                autoFocus
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
              <TextField
                margin="normal"
                required
                fullWidth
                name="password"
                label="Contraseña"
                type={showPassword ? 'text' : 'password'}
                id="password"
                autoComplete="current-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        aria-label="toggle password visibility"
                        onClick={() => setShowPassword(!showPassword)}
                        onMouseDown={handleMouseDownPassword}
                        edge="end"
                      >
                        {showPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />

              <Button
                type="submit"
                fullWidth
                variant="contained"
                disabled={loading}
                sx={{ mt: 3, mb: 2, py: 1.2 }}
              >
                {loading ? <CircularProgress size={24} /> : "Iniciar Sesión"}
              </Button>

              <Button
                fullWidth
                variant="outlined"
                startIcon={<GoogleIcon />}
                onClick={signInWithGoogle}
                disabled={loading}
                sx={{ mb: 2 }}
              >
                Continuar con Google
              </Button>

              <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 2 }}>
                <Button onClick={toggleResetPassword} size="small" sx={{ textTransform: 'none' }}>
                  ¿Olvidaste tu contraseña?
                </Button>
                <Button onClick={switchMode} size="small" sx={{ textTransform: 'none' }}>
                  Crear cuenta nueva
                </Button>
              </Box>
            </Box>
          )}
        </Box>
      </Paper>
    </Container>
  );
}

export default Login;
